import React, { useState } from 'react';
import { Mail, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ToastProvider';

// #312: Reiter „Postfach-Überwachung“ des Admin-Bereichs, Code unverändert
// aus AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// Postfächer und Vorlagen-Entwürfe lädt `loadData()` im Panel und reicht sie
// über die Setter herein. Die Entwürfe haben bewusst KEIN Autosave; dass sie
// den Reiterwechsel überstehen, hängt daran, dass das Panel diesen Hook aufruft.
// `loadData` ist das Neuladen des Panels (nach „Jetzt verarbeiten“).
export function useMailboxTab({ setError, setSaving, loadData }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  // Postfach-Überwachung State
  const [mailboxConfigs, setMailboxConfigs] = useState([]);
  const [showAddMailboxForm, setShowAddMailboxForm] = useState(false);
  const [newMailboxConfig, setNewMailboxConfig] = useState({
    mailboxAddress: '',
    enabled: true,
    checkIntervalSeconds: 60,
    processingSuccessFolder: 'Processed',
    processingErrorFolder: 'Processing-Errors',
    autoReplyNewTicketTemplate: '',
    autoReplyExistingTicketTemplate: ''
  });

  // Draft-State für Antwort-Vorlagen (kein Autosave beim Tippen)
  const [templateDrafts, setTemplateDrafts] = useState({}); // { [configId]: { autoReplyNewTicketTemplate, autoReplyExistingTicketTemplate } }

  // Postfach-Überwachung Handler
  const handleAddMailboxConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const response = await apiService.createMailboxConfig(newMailboxConfig);
      setMailboxConfigs([...mailboxConfigs, response.config]);
      setNewMailboxConfig({
        mailboxAddress: '',
        enabled: true,
        checkIntervalSeconds: 60,
        processingSuccessFolder: 'Processed',
        processingErrorFolder: 'Processing-Errors'
      });
      setShowAddMailboxForm(false);
      pushToast({ message: t('admin.mailbox.added', 'Postfach-Überwachung wurde erfolgreich hinzugefügt.'), type: 'success' });
    } catch (error) {
      console.error('Error adding mailbox config:', error);
      setError(t('admin.mailbox.addError', 'Fehler beim Hinzufügen der Postfach-Überwachung.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMailboxConfig = async (id, field, value) => {
    setSaving(true);
    setError(null);
    
    try {
      await apiService.updateMailboxConfig(id, { [field]: value });
      setMailboxConfigs(mailboxConfigs.map(config => 
        config.id === id ? { ...config, [field]: value } : config
      ));
      pushToast({ message: t('admin.mailbox.updated', 'Postfach-Einstellungen wurden erfolgreich aktualisiert.'), type: 'success' });
    } catch (error) {
      console.error('Error updating mailbox config:', error);
      setError(t('admin.mailbox.updateError', 'Fehler beim Aktualisieren der Postfach-Einstellungen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMailboxConfig = async (id) => {
    if (!(await confirm({ message: t('admin.mailbox.confirmDelete', 'Möchten Sie diese Postfach-Überwachung wirklich löschen?'), danger: true }))) {
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await apiService.deleteMailboxConfig(id);
      setMailboxConfigs(mailboxConfigs.filter(config => config.id !== id));
      pushToast({ message: t('admin.mailbox.deleted', 'Postfach-Überwachung wurde erfolgreich gelöscht.'), type: 'success' });
    } catch (error) {
      console.error('Error deleting mailbox config:', error);
      setError(t('admin.mailbox.deleteError', 'Fehler beim Löschen der Postfach-Überwachung.'));
    } finally {
      setSaving(false);
    }
  };

  // Helpers für Template-Drafts
  const getTemplateDraft = (id) => {
    const draft = templateDrafts[id] || { autoReplyNewTicketTemplate: '', autoReplyExistingTicketTemplate: '' };
    return draft;
  };

  const handleTemplateDraftChange = (id, field, value) => {
    setTemplateDrafts(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || { autoReplyNewTicketTemplate: '', autoReplyExistingTicketTemplate: '' }),
        [field]: value
      }
    }));
  };

  const handleSaveTemplateDrafts = async (id) => {
    setSaving(true);
    setError(null);
    try {
      const cfg = mailboxConfigs.find(c => c.id === id) || {};
      const draft = getTemplateDraft(id);
      const updates = {};
      if ((draft.autoReplyNewTicketTemplate || '') !== (cfg.autoReplyNewTicketTemplate || '')) {
        updates.autoReplyNewTicketTemplate = draft.autoReplyNewTicketTemplate || '';
      }
      if ((draft.autoReplyExistingTicketTemplate || '') !== (cfg.autoReplyExistingTicketTemplate || '')) {
        updates.autoReplyExistingTicketTemplate = draft.autoReplyExistingTicketTemplate || '';
      }
      if (Object.keys(updates).length === 0) {
        setSaving(false);
        return;
      }
      await apiService.updateMailboxConfig(id, updates);
      // Local state aktualisieren
      setMailboxConfigs(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
      pushToast({ message: 'Antwort-Vorlagen gespeichert.', type: 'success' });
    } catch (e) {
      console.error('Error saving template drafts:', e);
      setError(t('admin.templates.saveFailed', 'Fehler beim Speichern der Antwort-Vorlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplateDrafts = (id) => {
    const cfg = mailboxConfigs.find(c => c.id === id) || {};
    setTemplateDrafts(prev => ({
      ...prev,
      [id]: {
        autoReplyNewTicketTemplate: cfg.autoReplyNewTicketTemplate || '',
        autoReplyExistingTicketTemplate: cfg.autoReplyExistingTicketTemplate || ''
      }
    }));
  };

  const handleProcessNow = async (id) => {
    setSaving(true);
    setError(null);
    
    try {
      await apiService.processMailboxNow(id);
      pushToast({ message: t('admin.mailbox.processStarted', 'Manuelle Verarbeitung gestartet'), type: 'success' });
      loadData(); // Reload to update last processed time
    } catch (error) {
      console.error('Error processing mailbox:', error);
      setError(t('admin.mailbox.processError', 'Fehler bei der manuellen Verarbeitung'));
    } finally {
      setSaving(false);
    }
  };

  return { mailboxConfigs, setMailboxConfigs, showAddMailboxForm, setShowAddMailboxForm, newMailboxConfig, setNewMailboxConfig, templateDrafts, setTemplateDrafts, handleAddMailboxConfig, handleUpdateMailboxConfig, handleDeleteMailboxConfig, getTemplateDraft, handleTemplateDraftChange, handleSaveTemplateDrafts, handleResetTemplateDrafts, handleProcessNow };
}

const MailboxTab = ({ tab, saving, loading }) => {
  const { getLocale, t } = useI18n();
  const { mailboxConfigs, showAddMailboxForm, setShowAddMailboxForm, newMailboxConfig, setNewMailboxConfig, handleAddMailboxConfig, handleUpdateMailboxConfig, handleDeleteMailboxConfig, getTemplateDraft, handleTemplateDraftChange, handleSaveTemplateDrafts, handleResetTemplateDrafts, handleProcessNow } = tab;

  return (
    <div className="admin-section">
      <h3>{t('admin.mailbox.heading', 'Postfach-Überwachung')}</h3>
      <div className="settings-description">
        {t('admin.mailbox.desc', 'Konfigurieren Sie E-Mail-Postfächer für die automatische Ticket-Erstellung und -Zuordnung.')}
      </div>

      <div className="mailbox-configs-container">
        {mailboxConfigs.length === 0 ? (
          <div className="empty-state">
            <Mail size={48} style={{ color: 'var(--color-text-muted)', marginBottom: '16px' }} />
            <p>{t('admin.mailbox.noneTitle', 'Noch keine Postfächer konfiguriert.')}</p>
            <p>{t('admin.mailbox.noneDesc', 'Klicken Sie auf "Postfach hinzufügen", um die automatische E-Mail-Verarbeitung zu aktivieren.')}</p>
          </div>
        ) : (
          <div className="mailbox-configs-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('admin.mailbox.col.address', 'Postfach')}</th>
                  <th>{t('admin.mailbox.col.status', 'Status')}</th>
                  <th>{t('admin.mailbox.col.interval', 'Intervall')}</th>
                  <th>{t('admin.mailbox.col.successFolder', 'Erfolg-Ordner')}</th>
                  <th>{t('admin.mailbox.col.errorFolder', 'Fehler-Ordner')}</th>
                  <th>{t('admin.mailbox.col.lastProcessed', 'Letzte Verarbeitung')}</th>
                  <th>{t('admin.mailbox.col.actions', 'Aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {mailboxConfigs.map(config => (
                  <React.Fragment key={config.id}>
                  <tr>
                    <td className="mailbox-address">{config.mailboxAddress}</td>
                    <td>
                      <label className="status-toggle">
                        <input
                          type="checkbox"
                          checked={config.enabled}
                          onChange={(e) => handleUpdateMailboxConfig(config.id, 'enabled', e.target.checked)}
                          disabled={saving}
                        />
                        <span className={`toggle-slider ${config.enabled ? 'enabled' : 'disabled'}`}></span>
                        <span className="status-text">{config.enabled ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}</span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={config.checkIntervalSeconds}
                        onChange={(e) => handleUpdateMailboxConfig(config.id, 'checkIntervalSeconds', parseInt(e.target.value))}
                        min="30"
                        max="3600"
                        disabled={saving}
                        className="interval-input"
                      />s
                    </td>
                    <td>
                      <input
                        type="text"
                        value={config.processingSuccessFolder}
                        onChange={(e) => handleUpdateMailboxConfig(config.id, 'processingSuccessFolder', e.target.value)}
                        disabled={saving}
                        className="folder-input"
                        placeholder={t('admin.mailbox.successFolderLabel', 'Erfolg-Ordner')}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={config.processingErrorFolder}
                        onChange={(e) => handleUpdateMailboxConfig(config.id, 'processingErrorFolder', e.target.value)}
                        disabled={saving}
                        className="folder-input"
                        placeholder={t('admin.mailbox.errorFolderLabel', 'Fehler-Ordner')}
                      />
                    </td>
                    <td>
                      {config.lastProcessedAt ? (
                        <span className="last-processed">
                          {new Date(config.lastProcessedAt).toLocaleString(getLocale())}
                        </span>
                      ) : (
                        <span className="never-processed">{t('common.never', 'Nie')}</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleProcessNow(config.id)}
                          className="btn btn-primary btn-sm"
                          disabled={saving || config.isProcessing}
                          title={t('admin.mailbox.processNowTooltip', 'Jetzt verarbeiten')} aria-label={t('admin.mailbox.processNowTooltip', 'Jetzt verarbeiten')}
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteMailboxConfig(config.id)}
                          className="btn btn-danger btn-sm"
                          disabled={saving}
                          title={t('admin.mailbox.deleteTooltip', 'Postfach-Überwachung löschen')} aria-label={t('admin.mailbox.deleteTooltip', 'Postfach-Überwachung löschen')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="7">
                      <div className="template-fields">
                        <div className="form-group">
                          <label>Antwortmail neues Ticket</label>
                          <label className="status-toggle" style={{ marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="checkbox"
                              checked={!!config.autoReplyNewTicketEnabled}
                              onChange={(e) => handleUpdateMailboxConfig(config.id, 'autoReplyNewTicketEnabled', e.target.checked)}
                              disabled={saving}
                            />
                            <span className={`toggle-slider ${config.autoReplyNewTicketEnabled ? 'enabled' : 'disabled'}`}></span>
                            <span className="status-text">{config.autoReplyNewTicketEnabled ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}</span>
                          </label>
                          <textarea
                            rows="3"
                            value={getTemplateDraft(config.id).autoReplyNewTicketTemplate}
                            onChange={(e) => handleTemplateDraftChange(config.id, 'autoReplyNewTicketTemplate', e.target.value)}
                            placeholder={t('admin.templates.autoReplyPlaceholder', 'Wir haben ein neues Ticket mit der Ticketnummer [ticketnr] erstellt und melden uns schnellstmöglich.')}
                            className="settings-textarea"
                          />
                          <small>Variablen: [ticketnr]</small>
                        </div>
                        <div className="form-group">
                          <label>Antwortmail bestehendes Ticket</label>
                          <label className="status-toggle" style={{ marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="checkbox"
                              checked={!!config.autoReplyExistingTicketEnabled}
                              onChange={(e) => handleUpdateMailboxConfig(config.id, 'autoReplyExistingTicketEnabled', e.target.checked)}
                              disabled={saving}
                            />
                            <span className={`toggle-slider ${config.autoReplyExistingTicketEnabled ? 'enabled' : 'disabled'}`}></span>
                            <span className="status-text">{config.autoReplyExistingTicketEnabled ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}</span>
                          </label>
                          <textarea
                            rows="3"
                            value={getTemplateDraft(config.id).autoReplyExistingTicketTemplate}
                            onChange={(e) => handleTemplateDraftChange(config.id, 'autoReplyExistingTicketTemplate', e.target.value)}
                            placeholder="Ihre E-Mail wurde dem bestehenden Ticket [ticketnr] zugeordnet."
                            className="settings-textarea"
                          />
                          <small>Variablen: [ticketnr]</small>
                        </div>
                      </div>
                      <div className="template-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSaveTemplateDrafts(config.id)}
                          disabled={saving}
                          title="Antwort-Vorlagen speichern"
                        >
                          <Save size={14} /> {t('common.save', 'Speichern')}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleResetTemplateDrafts(config.id)}
                          disabled={saving}
                          title={t('admin.templates.discardChanges', 'Änderungen verwerfen')}
                        >
                          Zurücksetzen
                        </button>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mailbox-actions">
          <button
            onClick={() => setShowAddMailboxForm(!showAddMailboxForm)}
            className="btn btn-primary"
            disabled={saving}
          >
            <Plus size={16} />
            {t('admin.mailbox.addButton', 'Postfach hinzufügen')}
          </button>
        </div>

        {showAddMailboxForm && (
          <div className="add-mailbox-form">
            <h4>{t('admin.mailbox.addHeading', 'Neues Postfach hinzufügen')}</h4>
            <form onSubmit={handleAddMailboxConfig}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.mailbox.addrLabel', 'Postfach-Adresse *')}</label>
                  <input
                    type="email"
                    value={newMailboxConfig.mailboxAddress}
                    onChange={(e) => setNewMailboxConfig({...newMailboxConfig, mailboxAddress: e.target.value})}
                    required
                    placeholder="support@example.com"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.mailbox.intervalLabel', 'Prüfintervall (Sekunden)')}</label>
                  <input
                    type="number"
                    value={newMailboxConfig.checkIntervalSeconds}
                    onChange={(e) => setNewMailboxConfig({...newMailboxConfig, checkIntervalSeconds: parseInt(e.target.value)})}
                    min="30"
                    max="3600"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.mailbox.successFolderLabel', 'Erfolg-Ordner')}</label>
                  <input
                    type="text"
                    value={newMailboxConfig.processingSuccessFolder}
                    onChange={(e) => setNewMailboxConfig({...newMailboxConfig, processingSuccessFolder: e.target.value})}
                    placeholder="Processed"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.mailbox.errorFolderLabel', 'Fehler-Ordner')}</label>
                  <input
                    type="text"
                    value={newMailboxConfig.processingErrorFolder}
                    onChange={(e) => setNewMailboxConfig({...newMailboxConfig, processingErrorFolder: e.target.value})}
                    placeholder="Processing-Errors"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAddMailboxForm(false)} className="btn btn-secondary">
                  {t('common.cancel', 'Abbrechen')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving', 'Speichere...') : t('admin.mailbox.addSubmit', 'Hinzufügen')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailboxTab;
